import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, LineChart, Line, CartesianGrid } from "recharts";
import { STATUS_HEX, statusLabel } from "@/lib/format";
import { BarChart3 } from "lucide-react";

const PALETTE = ["#0284C7", "#059669", "#D97706", "#DC2626", "#6D28D9", "#0F766E", "#64748B", "#1D4ED8"];
const tip = { contentStyle: { fontSize: 12, borderRadius: 4, border: "1px solid #e2e8f0" } };

/** A chart must earn its space. Below the threshold we show the numbers as a sentence instead
 *  of drawing axes with nothing in them, and every chart carries a text alternative. */
export const ChartGuard = ({ data, minPoints = 2, summary, note, children, testId }) => {
  const rows = (data || []).filter((d) => (d?.count ?? d?.value ?? 0) !== 0);
  if (rows.length < minPoints) {
    return (
      <div className="px-4 py-5 text-sm text-slate-600" data-testid={testId ? `${testId}-summary` : "chart-summary"}>
        <BarChart3 size={18} className="text-slate-300 mb-1.5" aria-hidden />
        {summary || (rows.length === 0 ? "No data to chart yet." : rows.map((d) => `${statusLabel(d.key ?? d.name)}: ${d.count ?? d.value}`).join(" · "))}
        {note && <div className="text-xs text-slate-500 mt-1">{note}</div>}
      </div>
    );
  }
  return (
    <>
      {children}
      <p className="sr-only" data-testid={testId ? `${testId}-a11y` : undefined}>
        {summary || rows.map((d) => `${statusLabel(d.key ?? d.name)}: ${d.count ?? d.value}`).join(", ")}
      </p>
    </>
  );
};

export const StatusDonut = ({ data, onSelect, height = 200, minPoints = 2, summary }) => {
  const rows = (data || []).filter((d) => d.count > 0);
  return (
    <ChartGuard data={rows} minPoints={minPoints} summary={summary} testId="status-donut">
      <div className="flex items-center gap-3" data-testid="status-donut">
        <div style={{ width: height, height }}>
          <ResponsiveContainer><PieChart>
            <Pie data={rows} dataKey="count" nameKey="key" innerRadius="60%" outerRadius="92%" paddingAngle={2} onClick={(d) => onSelect?.(d.key)} cursor={onSelect ? "pointer" : "default"}>
              {rows.map((d) => <Cell key={d.key} fill={STATUS_HEX[d.key] || "#94A3B8"} />)}
            </Pie><Tooltip {...tip} formatter={(v, n) => [v, statusLabel(n)]} />
          </PieChart></ResponsiveContainer>
        </div>
        <ul className="text-xs space-y-1 flex-1 min-w-0">
          {rows.map((d) => <li key={d.key}><button className="flex items-center gap-2 w-full hover:underline text-left" onClick={() => onSelect?.(d.key)} data-testid={`donut-legend-${d.key}`}><span className="h-2.5 w-2.5 rounded-sm shrink-0" style={{ background: STATUS_HEX[d.key] || "#94A3B8" }} /><span className="truncate">{statusLabel(d.key)}</span><span className="ml-auto font-semibold tabular-nums">{d.count}</span></button></li>)}
        </ul>
      </div>
    </ChartGuard>
  );
};

export const BarList = ({ data, xKey = "key", yKey = "count", onSelect, height = 220, color = "#0284C7", label, horizontal = false, suffix = "", minPoints = 2, summary }) => (
  <ChartGuard data={(data || []).map((d) => ({ key: d[xKey], count: d[yKey] }))} minPoints={minPoints} summary={summary} testId="bar-chart">
    <div style={{ height }} data-testid="bar-chart">
      <ResponsiveContainer>
        <BarChart data={data || []} layout={horizontal ? "vertical" : "horizontal"} margin={{ top: 4, right: 8, left: horizontal ? 8 : -20, bottom: 4 }}>
          <CartesianGrid strokeDasharray="2 4" stroke="#e2e8f0" vertical={false} />
          {horizontal ? <><XAxis type="number" tick={{ fontSize: 11 }} /><YAxis type="category" dataKey={xKey} width={120} tick={{ fontSize: 11 }} /></> : <><XAxis dataKey={xKey} tick={{ fontSize: 11 }} interval={0} /><YAxis tick={{ fontSize: 11 }} /></>}
          <Tooltip {...tip} formatter={(v) => [`${v}${suffix}`, label || yKey]} />
          <Bar dataKey={yKey} fill={color} radius={[2, 2, 0, 0]} onClick={(d) => onSelect?.(d[xKey], d)} cursor={onSelect ? "pointer" : "default"}>
            {(data || []).map((d, i) => <Cell key={i} fill={d.color || color} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  </ChartGuard>
);

export const TrendLine = ({ data, xKey = "month", yKey = "completions", height = 200, minPoints = 3, summary }) => (
  <ChartGuard data={(data || []).map((d) => ({ key: d[xKey], count: d[yKey] }))} minPoints={minPoints} summary={summary} note="A trend needs at least three periods of data." testId="trend-chart">
    <div style={{ height }} data-testid="trend-chart"><ResponsiveContainer>
      <LineChart data={data || []} margin={{ top: 8, right: 12, left: -20, bottom: 4 }}>
        <CartesianGrid strokeDasharray="2 4" stroke="#e2e8f0" vertical={false} /><XAxis dataKey={xKey} tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
        <Tooltip {...tip} /><Line type="monotone" dataKey={yKey} stroke="#0284C7" strokeWidth={2} dot={{ r: 3 }} />
      </LineChart></ResponsiveContainer></div>
  </ChartGuard>
);

export const palette = PALETTE;
